import { Component, DoCheck, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Map, View, Extent, Layer, Feature, Overlay } from 'ol';
import { defaults as controlDefaults } from 'ol/control';
import { defaults as interactionDefaults } from 'ol/interaction';
import { LogService } from '../../../src/app/modules/domain/services/logging/log.service';
import { SearchData } from './search-popup/search-data';
import { Address } from '../../../src/app/modules/domain/models';
import { PopupData } from './update-popup/popup-data';

@Component({
    selector: 'app-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss']
})
export class MapComponent implements OnInit, OnChanges, OnDestroy, DoCheck {
    @ViewChild('map') mapElement: ElementRef;
    @Input() extent: Extent;
    @Input() projection: string;
    @Input() isSearching: boolean;
    @Input() popupData: PopupData;
    @Input() filteredAddresses: Address[];
    @Output() showPopup: EventEmitter<string> = new EventEmitter<string>();
    @Output() closePopup: EventEmitter<void> = new EventEmitter<void>();
    @Output() submitSearchData: EventEmitter<SearchData> = new EventEmitter<SearchData>();
    @Output() closeSearchPopup: EventEmitter<void> = new EventEmitter<void>();

    instance: Map;
    updatePopup: Overlay;
    searchPopup: Overlay;
    defaultProjection: string = 'EPSG:3857';

    constructor(
        private _log: LogService
    ) { }

    ngOnInit(): void {
        this.instance = this.initializeMap();
        this.fitExtentIntoView();
        this.updatePopup = new Overlay({
            element: document.getElementById('popup')
        });
        this.searchPopup = new Overlay({
            element: document.getElementById('search-popup')
        });
        this.instance.on('click', (event: any) => {
            let featureIds: string[] = [];
            this.instance.forEachFeatureAtPixel(event.pixel, function (feature: Feature, layer: Layer): any {
                if (feature.get('addressId')) {
                    featureIds.push(feature.get('addressId'));
                }
            });
            if (featureIds.length <= 0) { return; }
            this.updatePopup.setPosition(event.coordinate);
            this.showPopup.emit(featureIds[0]);
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.instance) {
            return;
        }
        if ('extent' in changes && changes['extent'].currentValue && changes['extent'].previousValue) {
            const noChange: boolean = changes['extent'].currentValue
                .every((coord: number, index: number) => coord === changes['extent'].previousValue[index]);
            if (!noChange) {
                this.fitExtentIntoView();
            }
        }
        if ('popupData' in changes && (changes['popupData'].currentValue !== changes['popupData'].previousValue)) {
            if (changes['popupData'].currentValue === undefined || changes['popupData'].currentValue === null) {
                this.instance.removeOverlay(this.updatePopup);
            } else {
                this.instance.removeOverlay(this.updatePopup);
                this.instance.addOverlay(this.updatePopup);
            }
        }
        if ('isSearching' in changes && (changes['isSearching'].currentValue !== changes['isSearching'].previousValue)) {
            if (this.isSearching) {
                this.instance.addOverlay(this.searchPopup);
            } else {
                this.instance.removeOverlay(this.searchPopup);
            }
        }
    }

    ngOnDestroy(): void {
        this.instance.setTarget(null);
    }

    ngDoCheck(): void {
        this.redrawMap();
    }

    fitExtentIntoView(): void {
        if (!this.extent) {
            this._log.error('MapComponent:', `Please provide an extent in ${this.defaultProjection}`);
            return;
        }
        this.instance.getView().fit(this.extent);
    }

    initializeMap(): Map {
        return new Map({
            controls: controlDefaults({ attribution: false }),
            interactions: interactionDefaults(),
            layers: [],
            target: this.mapElement.nativeElement,
            view: new View({
                projection: this.projection ? this.projection : this.defaultProjection,
                maxZoom: 20,
                minZoom: 5,
            })
        });
    }

    redrawMap(): void {
        this.instance.updateSize();
    }

    onSubmitSearchData(data: SearchData): void {
        this.submitSearchData.emit(data);
    }
    onZoomToFeature(id: string): void {
        const layer: Layer = this.instance.getLayers().getArray().find((l: Layer) => l.get('id') === 'address');
        const feature: Feature = layer.getSource().getFeatures().find((f: Feature) => f.get('addressId') === id);
        this.instance.getView().fit(feature.getGeometry().getExtent());
        this.instance.getView().setZoom(20);
        this.updatePopup.setPosition(feature.getGeometry().getCoordinates());
        this.instance.addOverlay(this.updatePopup);
        this.showPopup.emit(id);
    }
    onClosePopup(): void {
        this.closePopup.emit();
    }
    onCloseSearchPopup(): void {
        this.closeSearchPopup.emit();
    }
}



// WEBPACK FOOTER //
// ./libs/map/components/map.component.ts